/* eslint-disable prefer-destructuring */
import * as eff from 'redux-saga/effects';
import {
  selection,
} from 'redux-resource-plugins';
import createActionCreators from 'redux-resource-action-creators';

import {
  actionTypes,
  addResources,
  updateResources,
  deleteResources,
} from '../actions';
import {
  ID,
  getExerciseNumber,
  updateOrderNameExercises,
} from '../utils';
import {
  getSelectedIds,
  getResourceById,
  getDefaultProperty,
  getResourceMappedList,
  getSortedListWorkoutExercises,
} from '../selectors';
import {
  throwError,
} from './helpers';

import api from '../api';

export default function* exerciseFlow(reportChannel) {
  function* addToWorkout({
    workoutId,
    exerciseId,
    workoutType,
    callbacks,
  }) {
    const list = `workout_${workoutId}`;
    const resourceType = 'workoutExercises';
    const actions = createActionCreators('create', {
      resourceType,
      requestKey: 'addExercise',
    });

    yield eff.put(actions.pending());

    const exercises = yield eff.select(
      getSortedListWorkoutExercises(
        workoutId,
        true,
      ),
    );

    try {
      let position = 1;
      let orderName = '1';

      if (exercises.length) {
        const lastNumber = getExerciseNumber(
          exercises[exercises.length - 1],
        ).number;
        position = exercises.length + 1;
        orderName = lastNumber + 1;
      }

      const response = yield eff.call(
        api.createEntity,
        {
          type: 'workout_exercises',
          body: {
            position,
            order_name: orderName,
            workout_id: workoutId,
            exercise_id: exerciseId,
            workout_type: workoutType,
          },
        },
      );

      yield eff.put(actions.succeeded({
        resources: [{
          ...response,
          number: orderName,
          numeric: '',
        }],
        list,
      }));

      callbacks?.onSuccess?.(response);

      if (!exercises.length) {
        const defaultProperty = yield eff.select(getDefaultProperty);
        yield eff.put(
          addResources({
            resourceType: 'exerciseProperties',
            resources: [{
              id: ID(),
              isNew: true,
              position: 1,
              workout_exercise_id: response.id,
              ...defaultProperty,
            }],
            list: `workout_${workoutId}`,
          }),
        );
      }
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      callbacks?.onError?.(e);
      yield eff.call(throwError, e);
    }
  }

  function* addAlternative({
    workoutId,
    exerciseId,
    alternativeId,
    workoutType,
    callbacks,
  }) {
    const list = `workout_${workoutId}`;
    const resourceType = 'workoutExercises';
    const actions = createActionCreators('create', {
      resourceType,
      requestKey: 'addAlternative',
    });

    yield eff.put(actions.pending());

    const properties = yield eff.select(
      getResourceMappedList(
        'exerciseProperties',
        `workout_${workoutId}`,
      ),
    );

    const exercise = yield eff.select(
      getResourceById(
        'workoutExercises',
        exerciseId,
      ),
    );

    try {
      const response = yield eff.call(
        api.createEntity,
        {
          type: 'workout_exercises',
          body: {
            position: exercise.position,
            order_name: exercise.order_name,
            workout_id: workoutId,
            exercise_id: alternativeId,
            workout_type: workoutType,
          },
        },
      );

      yield eff.call(
        api.updateEntity,
        {
          id: exerciseId,
          type: 'workout_exercises',
          body: {
            is_selected_alternative: true,
          },
        },
      );

      const exerciseProperties = yield eff.all(
        properties
          .filter(p => p.workout_exercise_id === exerciseId)
          .map(
            (property) => eff.call(
              api.createEntity,
              {
                type: 'exercise_properties',
                body: {
                  value: property.value,
                  value2: property.value2,
                  position: property.position,
                  property_unit_id: property.property_unit_id,
                  workout_exercise_id: response.id,
                  personal_property_id: property.personal_property.id,
                },
              },
            ),
          ),
      );

      const exercises = yield eff.select(
        getSortedListWorkoutExercises(
          workoutId,
        ),
      );

      const hasFirstAlternative = exercises.every(
        (item: any) => item.alternatives.length === 0,
      );

      const customLists = {
        exerciseProperties: `workout_${workoutId}`,
      };

      yield eff.put(actions.succeeded({
        resources: [
          {
            ...response,
            letter: exercise.letter,
            number: exercise.number,
          },
          {
            id: exerciseId,
            hasFirstAlternative,
            is_selected_alternative: true,
          },
        ],
        list,
        customLists,
        includedResources: {
          exerciseProperties,
        },
      }));

      callbacks?.onSuccess?.(response);

      if (hasFirstAlternative) {
        yield eff.delay(3000);
        yield eff.put(
          updateResources({
            resourceType,
            resources: [
              {
                id: exerciseId,
                hasFirstAlternative: false,
              },
            ],
          }),
        );
      }
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      callbacks?.onError?.(e);
      yield eff.call(throwError, e);
    }
  }

  function* setAlternative({
    exerciseId,
    alternativeId,
    callbacks,
  }) {
    const resourceType = 'workoutExercises';
    const actions = createActionCreators('create', {
      resourceType,
      requestKey: 'setAlternative',
    });

    yield eff.put(actions.pending());

    try {
      const resources = [
        {
          id: exerciseId,
          is_selected_alternative: false,
        },
        {
          id: alternativeId,
          is_selected_alternative: true,
        },
      ];

      yield eff.all(
        resources.map(
          (resource) => eff.call(
            api.updateEntity,
            {
              id: resource.id,
              type: 'workout_exercises',
              body: {
                is_selected_alternative: resource.is_selected_alternative,
              },
            },
          ),
        ),
      );

      yield eff.put(
        actions.succeeded({
          resources,
        }),
      );

      callbacks?.onSuccess?.(resources);
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      callbacks?.onError?.(e);
      yield eff.call(throwError, e);
    }
  }

  function* replaceInWorkout({
    workoutId,
    exerciseId,
    newExerciseId,
    workoutType,
    callbacks,
  }) {
    const list = `workout_${workoutId}`;
    const resourceType = 'workoutExercises';
    const actions = createActionCreators('create', {
      resourceType,
      requestKey: 'addExercise',
    });

    yield eff.put(actions.pending());

    try {
      const shouldReplace = yield eff.select(
        getResourceById(
          'workoutExercises',
          exerciseId,
        ),
      );

      const properties = yield eff.select(
        getResourceMappedList(
          'exerciseProperties',
          `workout_${workoutId}`,
        ),
      );

      const response = yield eff.call(
        api.createEntity,
        {
          type: 'workout_exercises',
          body: {
            position: shouldReplace.position,
            order_name: shouldReplace.order_name,
            workout_id: workoutId,
            exercise_id: newExerciseId,
            workout_type: workoutType,
            is_selected_alternative: shouldReplace.is_selected_alternative,
          },
        },
      );

      const exerciseProperties = yield eff.all(
        properties
          .filter(p => p.workout_exercise_id === exerciseId)
          .map(
            (property) => eff.call(
              api.createEntity,
              {
                type: 'exercise_properties',
                body: {
                  value: property.value,
                  value2: property.value2,
                  position: property.position,
                  property_unit_id: property.property_unit_id,
                  property_unit_name: property.property_unit_name,
                  workout_exercise_id: response.id,
                  personal_property_id: property.personal_property.id,
                },
              },
            ),
          ),
      );

      // remove old
      yield eff.call(
        api.deleteEntity,
        {
          id: exerciseId,
          type: 'workout_exercises',
        },
      );

      yield eff.put(
        deleteResources({
          resourceType,
          resources: [{
            id: exerciseId,
          }],
        }),
      );

      const customLists = {
        exerciseProperties: `workout_${workoutId}`,
      };

      yield eff.put(actions.succeeded({
        resources: [{
          ...response,
          ...getExerciseNumber(response),
        }],
        list,
        customLists,
        includedResources: {
          exerciseProperties,
        },
      }));

      callbacks?.onSuccess?.(response);
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      callbacks?.onError?.(e);
      yield eff.call(throwError, e);
    }
  }

  // Set orderName
  function* setOrderName({
    workoutId,
    exerciseId,
    newNumber,
  }) {
    const resourceType = 'workoutExercises';

    const actions = createActionCreators('update', {
      resourceType,
      requestKey: 'setOrderName',
    });
    yield eff.put(actions.pending());

    const exercises = yield eff.select(
      getSortedListWorkoutExercises(
        workoutId,
      ),
    );

    try {
      const rest = exercises.filter(e => e.id !== exerciseId);
      const exercise = exercises.find(e => e.id === exerciseId);
      const newSiblings = exercises.filter(e => e.number === newNumber);

      if (newSiblings.length) {
        const firstSiblingIndex = rest.findIndex(e => e.id === newSiblings[0].id);
        const lastSiblingIndex = firstSiblingIndex + newSiblings.length;
        const isAbove = exercise.position > newSiblings[0].position;

        const newIndex = (isAbove)
          ? lastSiblingIndex
          : firstSiblingIndex;

        rest.splice(
          newIndex,
          0,
          {
            ...exercise,
            letter: isAbove ? 'A' : getNextLetter(newSiblings[lastSiblingIndex]?.letter),
            number: newNumber,
          },
        );
      } else {
        rest.push({
          ...exercise,
          number: newNumber,
        });
      }

      const resources = updateOrderNameExercises(rest);

      yield eff.all(
        resources.map(item => eff.call(
          api.updateEntity,
          {
            id: item.id,
            type: 'workout_exercises',
            body: {
              position: item.position,
              order_name: item.order_name,
            },
          },
        )),
      );

      yield eff.put(actions.succeeded({
        resources,
      }));
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* saveProperty({
    propertyId,
    payload,
    isNew,
    list,
    callbacks,
  }) {
    const resourceType = 'exerciseProperties';

    const actions = createActionCreators(isNew ? 'create' : 'update', {
      resourceType,
      requestKey: 'saveExerciseProperty',
    });
    yield eff.put(actions.pending());

    try {
      const response = yield eff.call(
        isNew ? api.createEntity : api.updateEntity,
        {
          id: propertyId,
          body: payload,
          type: 'exercise_properties',
        },
      );

      if (isNew) {
        // delete the old from local state
        yield eff.put(deleteResources({
          resourceType,
          resources: [{
            id: propertyId,
          }],
        }));
      }

      yield eff.put(actions.succeeded({
        resources: [response],
        list,
      }));

      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
      if (callbacks?.onSuccess) callbacks.onSuccess(response);
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
      if (callbacks?.onError) callbacks.onError(e);
    }
  }

  function* saveDieRollOptions({
    properties,
    callbacks,
  }) {
    const resourceType = 'exerciseProperties';

    const actions = createActionCreators('create', {
      resourceType,
      requestKey: 'saveDieRollOptions',
    });
    yield eff.put(actions.pending());

    try {
      const resources = yield eff.all(
        properties.map(({ id, ...body }) => eff.call(
          api.updateEntity,
          {
            id,
            body,
            type: 'exercise_properties',
          },
        )),
      );

      yield eff.put(actions.succeeded({
        resources,
      }));

      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
      if (callbacks?.onSuccess) callbacks.onSuccess(resources);
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
      if (callbacks?.onError) callbacks.onError(e);
    }
  }

  function* assignProperties({ workoutId }) {
    const list = `workout_${workoutId}`;
    const resourceType = 'exerciseProperties';
    const actions = createActionCreators('create', {
      resourceType,
      requestKey: 'assignProperties',
    });
    yield eff.put(actions.pending());

    const selectedIds = yield eff.select(
      getSelectedIds('workoutExercises'),
    );

    const allProperties = yield eff.select(
      getResourceMappedList(resourceType, list),
    );

    const workoutProperties = allProperties.filter(
      p => (p.workout_id === workoutId),
    );

    const validProperties = workoutProperties.filter(
      p => (p.personal_property_id && p.property_unit_id),
    );

    try {
      const result = yield eff.all(
        selectedIds.reduce((acc, id) => [
          ...acc,
          ...validProperties.map((property, index) => eff.call(
            api.createEntity,
            {
              type: 'exercise_properties',
              body: {
                position: (
                  [
                    ...allProperties,
                    ...validProperties,
                  ].filter(
                    p => p.workout_exercise_id === id,
                  ).length + 1 + index
                ),
                value: property.value,
                value2: property.value2,
                property_unit_id: property.property_unit_id,
                personal_property_id: property.personal_property_id,
                workout_exercise_id: id,
              },
            },
          )),
        ], []),
      );

      if (!selectedIds.length) {
        yield eff.put(reportChannel,
          {
            messageType: 'notify',
            message: {
              type: 'warn',
              key: 'exercise.empty_assign_exercise',
            },
          });

        yield eff.put(actions.failed({
          resources: [],
        }));
      } else {
        yield eff.put(actions.succeeded({
          resources: result,
          list,
        }));

        yield eff.put(deleteResources({
          resourceType,
          resources: workoutProperties,
        }));

        yield eff.put(
          selection.clearSelectedResources('workoutExercises'),
        );
      }
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      yield eff.call(throwError, e);
    }
  }

  function* unAssignFromClient({
    id,
    clientId,
    resourceType,
    callbacks,
  }) {
    const actions = createActionCreators('delete', {
      resourceType,
      resources: [id],
      requestKey: 'deleteEntity',
    });

    try {
      yield eff.put(actions.pending());

      yield eff.call(
        api.deleteEntity,
        {
          // id,
          qs: {
            exercise_id: id,
            person_id: clientId,
          },
          type: 'personal_exercises',
        },
      );

      yield eff.put(actions.succeeded({
        resources: [id],
      }));

      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
      if (callbacks?.onSuccess) callbacks.onSuccess();
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));
      if (callbacks?.setSubmitting) callbacks.setSubmitting(false);
      if (callbacks?.onError) callbacks.onError(e);

      yield eff.call(throwError, e, reportChannel);
    }
  }

  yield eff.takeEvery(actionTypes.REQUEST_ADD_EXERCISE_TO_WORKOUT, addToWorkout);
  yield eff.takeEvery(actionTypes.REQUEST_REPLACE_EXERCISE_IN_WORKOUT, replaceInWorkout);
  yield eff.takeEvery(actionTypes.REQUEST_ADD_ALTERNATIVE_EXERCISE, addAlternative);
  yield eff.takeEvery(actionTypes.REQUEST_SET_ALTERNATIVE_EXERCISE, setAlternative);
  yield eff.takeEvery(actionTypes.REQUEST_SET_EXERCISE_ORDER_NAME, setOrderName);
  yield eff.takeEvery(actionTypes.REQUEST_UNASSIGN_FROM_CLIENT, unAssignFromClient);

  yield eff.takeEvery(actionTypes.REQUEST_SAVE_EXERCISE_PROPERTY, saveProperty);
  yield eff.takeEvery(actionTypes.REQUEST_SAVE_DIE_ROLL_OPTIONS, saveDieRollOptions);
  yield eff.takeEvery(actionTypes.REQUEST_ASSIGN_PROPERTIES, assignProperties);
}
